<template>
    <v-card class="habit-card px-4 py-2 text-primary"
            variant="outlined">
        <v-card-title>
            <v-row justify="space-between">
                <v-col>{{name}}</v-col>
            <v-col cols="auto">
                <v-tooltip location="bottom"
                           text="Редактировать">
                    <template v-slot:activator="{ props }">
                        <v-btn @click="showEditHabitDialog = true"
                               density="comfortable"
                               variant="text"
                               color="primary"
                               v-bind="props"
                               icon="mdi-pen" />
                    </template>
                </v-tooltip>
                <v-tooltip location="bottom"
                           text="Удалить">
                    <template v-slot:activator="{ props }">
                        <v-btn @click="habitToDelete = { id, name }"
                               density="comfortable"
                               variant="text"
                               color="error"
                               v-bind="props"
                               icon="mdi-close" />
                    </template>
                </v-tooltip>
            </v-col>
            </v-row>
        </v-card-title>

        <v-card-text class="d-flex flex-wrap">
            <div v-for="week in weeks" :key="week[0].date.day" class="d-flex flex-row">
                <div v-for="day in week" :key="day.number + '-' + day.date.day"
                     class="habit-btn d-flex"
                     :class="getClass(day)"
                     @click="setIsDone(day)">
                    <v-tooltip location="bottom"
                               :text="getDate(day.date)">
                        <template v-slot:activator="{ props }">
                            <span v-if="day.isDone"
                                  v-bind="props"
                                  class="ma-auto align-self-start">{{ day.number }}</span>
                            <span v-else
                                  v-bind="props"
                                  class="ma-auto align-self-start">{{ day.date.day }}</span>
                        </template>
                    </v-tooltip>
                </div>
            </div>
            <div v-if="days.length > 7"
                 class="habit-btn text-lonely d-flex"
                 @click="tryToDeleteDays">
                <v-tooltip location="bottom"
                           text="Удалить 7 дней">
                    <template v-slot:activator="{ props }">
                        <v-btn density="compact"
                               v-bind="props"
                               class="ma-auto align-self-start"
                               variant="text"
                               color="lonely"
                               icon="mdi-minus" />
                    </template>
                </v-tooltip>
            </div>
            <div class="habit-btn text-lonely d-flex"
                 @click="addDays">
                <v-tooltip location="bottom"
                           text="Добавить 7 дней">
                    <template v-slot:activator="{ props }">
                        <v-btn density="compact"
                               v-bind="props"
                               class="ma-auto align-self-start"
                               variant="text"
                               color="lonely"
                               icon="mdi-plus" />
                    </template>
                </v-tooltip>
            </div>
        </v-card-text>

        <habit-delete-confirm-dialog v-if="habitToDelete !== null" @hide="habitToDelete = null" :habitToDelete="habitToDelete" />
        <habit-creation-dialog v-if="showEditHabitDialog" @hide="showEditHabitDialog = false" @create:habit="editServerHabit($event)" :habit="{ name, startDate, days }" />
        <habit-days-delete-confirm-dialog v-if="showDeleteDaysDialog" @hide="showDeleteDaysDialog = false" @delete-days="deleteDays"></habit-days-delete-confirm-dialog>
    </v-card>
</template>

<script>
    import _debounce from "lodash/debounce";
    import HabitDeleteConfirmDialog from "@/components/client/habits/HabitDeleteConfirmDialog.vue";
    import HabitCreationDialog from "@/components/client/habits/HabitCreationDialog.vue";
    import HabitDaysDeleteConfirmDialog from "@/components/client/habits/HabitDaysDeleteConfirmDialog.vue";

    export default {
        props: {
            habit: Object
        },

        components: {
            HabitDeleteConfirmDialog,
            HabitCreationDialog,
            HabitDaysDeleteConfirmDialog
        },

        data() {
            return {
                id: this.habit.id,
                name: this.habit.name,
                isCompleted: this.habit.isCompleted,
                startDate: this.habit.startDate,
                endDate: this.habit.endDate,
                doneDaysCount: this.habit.doneDaysCount,
                startDayIndex: this.habit.startDayIndex,
                days: this.habit.days,
                habitToDelete: null,
                showEditHabitDialog: false,
                showDeleteDaysDialog: false,
                weekDaysCount: 7
            };
        },

        created() {
            this.$store.commit("setCurrentDateTime");

            this.days = this.habit.days;

            this.updateDays();
        },

        computed: {
            weeks() {
                const weeks = [];

                const weeksCount = Math.ceil(this.days.length / 7);

                for (let i = 0; i < weeksCount; i++) {
                    const startIndex = i * 7;
                    const weekDays = this.days.slice(startIndex, startIndex + 7);
                    weeks.push(weekDays);
                }

                return weeks;
            },

            currentDateTime() {
                return this.$store.state.currentDateTime;
            },

            currentLocale() {
                return this.$store.state.currentLocale;
            },

            newHabitValue() {
                return {
                    id: this.id,
                    name: this.name,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    startDayIndex: this.startDayIndex,
                    doneDaysCount: this.doneDaysCount,
                    isCompleted: this.isCompleted,
                    days: this.days
                };
            },

            lastWeekDays() {
                if (this.days.length < this.weekDaysCount) {
                    return null;
                }

                return this.days.slice(-this.weekDaysCount);
            }
        },

        watch: {
            habit(value) {
                this.name = value.name;
                this.isCompleted = value.isCompleted;
                this.startDate = value.startDate;
                this.endDate = value.endDate;
                this.doneDaysCount = value.doneDaysCount;
                this.startDayIndex = value.startDayIndex;
                this.days = value.days;

                this.updateDays();
            }
        },

        methods: {
            createDays(days) {
                if (days.length > 0) {
                    return days;
                }

                const weekday = this.currentDateTime.weekday;

                this.startDayIndex = weekday - 1;

                const startDate = this.currentDateTime.minus({ days: weekday - 1 });

                const newDays = [];
                const newDaysCount = 21;

                for (let i = 0; i < newDaysCount; i++) {
                    newDays.push({
                        index: i,
                        number: 0,
                        date: startDate.plus({ days: i }),
                        isDone: null
                    });
                }

                return newDays;
            },

            addDays() {
                const newDays = [];
                const newDaysCount = 7;
                const startDate = this.days[this.days.length - 1].date.plus({ days: 1 });
                let endDate = startDate;

                for (let i = 0; i < newDaysCount; i++) {
                    const date = startDate.plus({ days: i });

                    newDays.push({
                        index: this.days.length + i,
                        number: 0,
                        date: date,
                        isDone: null
                    });

                    endDate = date;
                }

                this.endDate = endDate;
                this.days = this.days.concat(newDays);

                this.updateHabit(this.newHabitValue, this);
            }, 

            tryToDeleteDays() {
                if (this.lastWeekDays !== null && this.lastWeekDays.some(d => d.isDone !== null)) {
                    this.showDeleteDaysDialog = true;
                    return;
                }

                this.deleteDays();
            },

            deleteDays() {
                this.days = this.days.slice(0, this.days.length - 7);

                this.updateHabit(this.newHabitValue, this);
            },

            updateDays() {
                let number = 0;
                let maxNumber = 0;

                this.days.sort((d1, d2) => d1.index - d2.index);

                this.days.forEach(d => {
                    if (d.isDone) {
                        number++;
                        d.number = number;
                        maxNumber = number;
                    } else {
                        d.number = 0;
                    }
                });

                this.doneDaysCount = maxNumber;
            },

            isActive(day) {
                return day.index >= this.startDayIndex
                    && day.date.startOf("day") <= this.currentDateTime.startOf("day");
            },

            isPreviousStartDay(day) {
                return day.index < this.startDayIndex;
            },

            setIsDone(day) {
                if (!this.isActive(day)) {
                    return;
                }

                day.isDone = day.isDone === null
                    ? true
                    : !day.isDone;

                this.updateDays();
                this.updateHabit(this.newHabitValue, this);
            },

            isRest(day) {
                return day.date.weekday === 6 || day.date.weekday === 7;
            },

            isCurrent(day) {
                return day.date.hasSame(this.currentDateTime, "day");
            },

            getClass(day) {
                let classString = "";

                if (this.isActive(day)) {
                    classString += " active-day";
                }

                if (this.isCurrent(day)) {
                    classString += " current-day";
                } else if (this.isRest(day)) {
                    classString += " rest-day";
                }

                if (day.isDone === true) {
                    classString += " bg-success no-border";
                } else if (day.isDone === false) {
                    classString += " bg-error no-border";
                } else if (this.isActive(day)) {
                    if (this.isRest(day) && !this.isCurrent(day)) {
                        classString += " bg-primary no-border";
                    } else {
                        classString += " text-primary";
                    }
                } else if (this.isPreviousStartDay(day)) {
                    if (this.isRest(day)) {
                        classString += " bg-grey-lighten-1 no-border text-white";
                    } else {
                        classString += " text-grey-lighten-2";
                    }
                } else {
                    if (this.isRest(day)) {
                        classString += " bg-lonely no-border";
                    } else {
                        classString += " text-lonely";
                    }
                }

                return classString;
            },

            getDate(dateTime) {
                return dateTime.setLocale(this.currentLocale).toLocaleString({ day: "numeric", month: "long", weekday: "long" });
            },

            updateHabit: _debounce(
                (habit, self) => {
                    const request = {
                        id: habit.id,
                        name: habit.name,
                        startDate: habit.startDate.toFormat("yyyy-MM-dd"),
                        endDate: habit.endDate.toFormat("yyyy-MM-dd"),
                        startDayIndex: habit.startDayIndex,
                        isCompleted: habit.isCompleted,
                        days: habit.days
                            .filter(d => d.index >= habit.startDayIndex)
                            .map(d => ({
                                id: d.id,
                                index: d.index,
                                date: d.date.toFormat("yyyy-MM-dd"),
                                isDone: d.isDone
                            }))
                    };

                    self.$store.dispatch("editHabit", request)
                        .then(() => self.$store.dispatch("loadHabits"));
                },
                600),

            editServerHabit(habit) {
                const request = {
                    id: this.id,
                    name: habit.name,
                    startDate: habit.startDate.toFormat("yyyy-MM-dd"),
                    startDayIndex: habit.startDayIndex
                };

                this.$store.dispatch("editHabit", request)
                    .then(() => this.$store.dispatch("loadHabits"));
            }
        }
    };
</script>

<style lang="scss">
    .habit-card .habit-btn {
        width: 40px;
        height: 40px;
        border: solid 2px;
        margin: 2px;
    }

    .habit-card .habit-btn.active-day {
        cursor: pointer;
    }

    .habit-card .habit-btn.no-border {
        border: none;
    }

    .habit-card .habit-btn.current-day {
        border-radius: 20px;
    }

    .habit-card .habit-btn.rest-day {
        border-top-right-radius: 20px;
    }
</style>